import { Bytes } from 'ethers';
import {
    AddMarginToPositionParamsStruct,
    ClosePositionParamsStruct,
    ConstraintsStruct,
    ExtendPositionParamsStruct,
    ExtendPositionToLeverageParamsStruct,
    OpenPositionParamsStruct,
    PartiallyClosePositionParamsStruct,
    RemoveMarginFromPositionParamsStruct,
} from '../contracts/typechain/ITradeManager';

export enum OrderBookPath {
    ALL_ORDERS = '/orders',
    ORDER_UPDATES = '/orderUpdates',
    TRADE_PAIR_LOOKUP = '/tradePairLookups',
    USER_ORDERS = '/orders/user/:userId',
    TRADE_PAIR_POSITIONS = '/positions/tp/:tradePairs',
    POSITION_HISTORY = '/positions/history',
    OPEN_POSITION = '/positions/open',
    EXTEND = '/positions/extend',
    EXTEND_LEVERAGE = '/positions/extendLeverage',
    ADD_MARGIN = '/positions/addMargin',
    REMOVE_MARGIN = '/positions/removeMargin',
    CLOSE_POSITION = '/positions/close',
    PARTIALLY_CLOSE = '/positions/partiallyClose',
    CANCEL_ORDER = '/positions/cancel',
}

export enum OrderBookType {
    OPEN = 'open',
    CLOSE = 'close',
    EXTEND = 'extend',
    EXTEND_LEVERAGE = 'extend-leverage',
    PARTIALLY_CLOSE = 'partially-close',
    ADD_MARGIN = 'add-margin',
    REMOVE_MARGIN = 'remove-margin',
    LIQUIDATED = 'liquidated',
    TAKE_PROFIT = 'take-profit',
    STOP_LOSS = 'stop-loss',
}

export enum OrderBookAction {
    OPEN_POSITION = 'Open Position',
    CLOSE_POSITION = 'Close Position',
    LIQUIDATED = 'Liquidated',
    PARTIALLY_CLOSE = 'Closed Partially',
    EXTEND_LEVERAGE = 'Adjust Leverage',
    ADD_MARGIN = 'Deposit Collateral',
    REMOVE_MARGIN = 'Withdraw Collateral',
    EXTEND = 'Increase Size',
    TAKE_PROFIT = 'Take Profit',
    STOP_LOSS = 'Stop Loss',
}

export type OrderType = {
    id: string;
    internalId: string;
    maker: string;
    orderJson: string;
    signature: Bytes;
    sigHash: string;
    orderType: OrderBookType;
    isShort: boolean;
    status:
        | 'requested'
        | 'executed'
        | 'expired'
        | 'cancelled'
        | 'error'
        | 'liquidated'
        | 'pending';
    liquidationPrice: string;
    type?: OrderBookType.TAKE_PROFIT | OrderBookType.STOP_LOSS;
    txHash: string | undefined;
    isMarketOrder: boolean;
    updatedAt: EpochTimeStamp;
    createdAt: EpochTimeStamp;
};

export type OrderBookParamMapper = {
    [OrderBookType.OPEN]: OpenPositionParamsStruct;
    [OrderBookType.CLOSE]: ClosePositionParamsStruct;
    [OrderBookType.PARTIALLY_CLOSE]: PartiallyClosePositionParamsStruct;
    [OrderBookType.EXTEND_LEVERAGE]: ExtendPositionToLeverageParamsStruct;
    [OrderBookType.ADD_MARGIN]: AddMarginToPositionParamsStruct;
    [OrderBookType.REMOVE_MARGIN]: RemoveMarginFromPositionParamsStruct;
    [OrderBookType.EXTEND]: ExtendPositionParamsStruct;
};

export type ResStatus = {
    status: number;
    data: OrderType[];
};

export type OrderDataTypes<T> = {
    params: T;
    constraints: ConstraintsStruct;
    salt: number;
};

export type OrderBookParamStructs =
    | OpenPositionParamsStruct
    | ClosePositionParamsStruct
    | PartiallyClosePositionParamsStruct
    | ExtendPositionToLeverageParamsStruct
    | AddMarginToPositionParamsStruct
    | RemoveMarginFromPositionParamsStruct
    | ExtendPositionParamsStruct;

export type OrderMessageType = {
    params: OrderBookParamStructs;
    constraints: ConstraintsStruct;
    signatureHash: string;
    salt: EpochTimeStamp;
};

export const OrderBookPathMapper: Record<
    Exclude<
        OrderBookType,
        OrderBookType.LIQUIDATED | OrderBookType.TAKE_PROFIT | OrderBookType.STOP_LOSS
    >,
    OrderBookPath
> = {
    [OrderBookType.OPEN]: OrderBookPath.OPEN_POSITION,
    [OrderBookType.CLOSE]: OrderBookPath.CLOSE_POSITION,
    [OrderBookType.PARTIALLY_CLOSE]: OrderBookPath.PARTIALLY_CLOSE,
    [OrderBookType.EXTEND_LEVERAGE]: OrderBookPath.EXTEND_LEVERAGE,
    [OrderBookType.ADD_MARGIN]: OrderBookPath.ADD_MARGIN,
    [OrderBookType.REMOVE_MARGIN]: OrderBookPath.REMOVE_MARGIN,
    [OrderBookType.EXTEND]: OrderBookPath.EXTEND,
};

export const OrderBookActionMapper: Record<OrderBookType, OrderBookAction> = {
    [OrderBookType.OPEN]: OrderBookAction.OPEN_POSITION,
    [OrderBookType.CLOSE]: OrderBookAction.CLOSE_POSITION,
    [OrderBookType.LIQUIDATED]: OrderBookAction.LIQUIDATED,
    [OrderBookType.PARTIALLY_CLOSE]: OrderBookAction.PARTIALLY_CLOSE,
    [OrderBookType.EXTEND_LEVERAGE]: OrderBookAction.EXTEND_LEVERAGE,
    [OrderBookType.ADD_MARGIN]: OrderBookAction.ADD_MARGIN,
    [OrderBookType.REMOVE_MARGIN]: OrderBookAction.REMOVE_MARGIN,
    [OrderBookType.EXTEND]: OrderBookAction.EXTEND,
    [OrderBookType.TAKE_PROFIT]: OrderBookAction.TAKE_PROFIT,
    [OrderBookType.STOP_LOSS]: OrderBookAction.STOP_LOSS,
};

export const OrderBookDomain = (chainId: number, verifyingContract: string) => ({
    name: 'UnlimitedLeverage',
    version: '1',
    chainId,
    verifyingContract,
});

const OrderBookConstraint = [
    { name: 'deadline', type: 'uint256' },
    { name: 'minPrice', type: 'int256' },
    { name: 'maxPrice', type: 'int256' },
] as const;

const EIP712Domain = [
    { name: 'name', type: 'string' },
    { name: 'version', type: 'string' },
    { name: 'chainId', type: 'uint256' },
    { name: 'verifyingContract', type: 'address' },
] as const;

export const OrderBookActionTypes = {
    [OrderBookType.OPEN]: {
        EIP712Domain,
        OpenPositionOrder: [
            { name: 'params', type: 'OpenPositionParams' },
            { name: 'constraints', type: 'Constraints' },
            { name: 'salt', type: 'uint256' },
        ],
        Constraints: OrderBookConstraint,
        OpenPositionParams: [
            { name: 'tradePair', type: 'address' },
            { name: 'margin', type: 'uint256' },
            { name: 'leverage', type: 'uint256' },
            { name: 'isShort', type: 'bool' },
            { name: 'referrer', type: 'address' },
            { name: 'whitelabelAddress', type: 'address' },
        ],
    },
    [OrderBookType.CLOSE]: {
        EIP712Domain,
        ClosePositionOrder: [
            { name: 'params', type: 'ClosePositionParams' },
            { name: 'constraints', type: 'Constraints' },
            { name: 'signatureHash', type: 'bytes32' },
            { name: 'salt', type: 'uint256' },
        ],
        Constraints: OrderBookConstraint,
        ClosePositionParams: [
            { name: 'tradePair', type: 'address' },
            { name: 'positionId', type: 'uint256' },
        ],
    },
    [OrderBookType.EXTEND]: {
        EIP712Domain,
        ExtendPositionOrder: [
            { name: 'params', type: 'ExtendPositionParams' },
            { name: 'constraints', type: 'Constraints' },
            { name: 'signatureHash', type: 'bytes32' },
            { name: 'salt', type: 'uint256' },
        ],
        Constraints: OrderBookConstraint,
        ExtendPositionParams: [
            { name: 'tradePair', type: 'address' },
            { name: 'positionId', type: 'uint256' },
            { name: 'addedMargin', type: 'uint256' },
            { name: 'addedLeverage', type: 'uint256' },
        ],
    },
    [OrderBookType.EXTEND_LEVERAGE]: {
        EIP712Domain,
        ExtendPositionToLeverageOrder: [
            { name: 'params', type: 'ExtendPositionToLeverageParams' },
            { name: 'constraints', type: 'Constraints' },
            { name: 'signatureHash', type: 'bytes32' },
            { name: 'salt', type: 'uint256' },
        ],
        Constraints: OrderBookConstraint,
        ExtendPositionToLeverageParams: [
            { name: 'tradePair', type: 'address' },
            { name: 'positionId', type: 'uint256' },
            { name: 'targetLeverage', type: 'uint256' },
        ],
    },
    [OrderBookType.PARTIALLY_CLOSE]: {
        EIP712Domain,
        PartiallyClosePositionOrder: [
            { name: 'params', type: 'PartiallyClosePositionParams' },
            { name: 'constraints', type: 'Constraints' },
            { name: 'signatureHash', type: 'bytes32' },
            { name: 'salt', type: 'uint256' },
        ],
        Constraints: OrderBookConstraint,
        PartiallyClosePositionParams: [
            { name: 'tradePair', type: 'address' },
            { name: 'positionId', type: 'uint256' },
            { name: 'proportion', type: 'uint256' },
        ],
    },
    [OrderBookType.ADD_MARGIN]: {
        EIP712Domain,
        AddMarginToPositionOrder: [
            { name: 'params', type: 'AddMarginToPositionParams' },
            { name: 'constraints', type: 'Constraints' },
            { name: 'signatureHash', type: 'bytes32' },
            { name: 'salt', type: 'uint256' },
        ],
        Constraints: OrderBookConstraint,
        AddMarginToPositionParams: [
            { name: 'tradePair', type: 'address' },
            { name: 'positionId', type: 'uint256' },
            { name: 'addedMargin', type: 'uint256' },
        ],
    },
    [OrderBookType.REMOVE_MARGIN]: {
        EIP712Domain,
        RemoveMarginFromPositionOrder: [
            { name: 'params', type: 'RemoveMarginFromPositionParams' },
            { name: 'constraints', type: 'Constraints' },
            { name: 'signatureHash', type: 'bytes32' },
            { name: 'salt', type: 'uint256' },
        ],
        Constraints: OrderBookConstraint,
        RemoveMarginFromPositionParams: [
            { name: 'tradePair', type: 'address' },
            { name: 'positionId', type: 'uint256' },
            { name: 'removedMargin', type: 'uint256' },
        ],
    },
} as const;
